/* Pour-conditions warning log — proof of what we told the customer about the
   weather and what they told us back. Printable for the job file. */
import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { CloudRain, Check, Clock, Phone, AlertTriangle, Printer, FileText, Loader2 } from "lucide-react";

const OUTCOME = {
  go_ahead: { label: "Go ahead", Icon: Check, cls: "border-emerald-500 text-emerald-400 bg-emerald-500/10" },
  delay: { label: "Delay requested", Icon: Clock, cls: "border-red-500 text-red-400 bg-red-500/10" },
  call_me: { label: "Call me", Icon: Phone, cls: "border-amber-500 text-amber-400 bg-amber-500/10" },
};
const NO_ANSWER = { label: "No answer", Icon: AlertTriangle, cls: "border-red-500 text-red-400 bg-red-500/10" };

const fmt = (iso) => (iso ? new Date(iso).toLocaleString([], { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");

export default function PourWarnings() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (from) qs.set("date_from", from);
      if (to) qs.set("date_to", to);
      const { data: d } = await api.get(`/analytics/pour-warnings?${qs}`);
      setData(d);
      setFrom((f) => f || d.date_from);
      setTo((t) => t || d.date_to);
    } catch (e) {
      toast.error("Couldn't load the warning log");
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const rows = useMemo(() => {
    const all = data?.rows || [];
    if (filter === "all") return all;
    if (filter === "no_answer") return all.filter((r) => !r.reply_code);
    return all.filter((r) => r.reply_code === filter);
  }, [data, filter]);

  const sm = data?.summary || {};
  const pdfUrl = (jobId) => {
    const qs = new URLSearchParams();
    if (from) qs.set("date_from", from);
    if (to) qs.set("date_to", to);
    if (jobId) qs.set("job_id", jobId);
    return `${process.env.REACT_APP_BACKEND_URL}/api/analytics/pour-warnings.pdf?${qs}`;
  };

  return (
    <div className="p-6 lg:p-8 space-y-5" data-testid="pour-warnings-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="label-tag flex items-center gap-2"><CloudRain className="w-3.5 h-3.5 text-primary-orange" /> Quality &amp; Risk</div>
          <h1 className="heading text-3xl sm:text-4xl font-black uppercase mt-0.5">Pour Warnings</h1>
          <p className="text-sm text-muted-industrial mt-1 max-w-2xl">
            Every adverse-conditions warning sent to a customer, the exact words we sent, and the instruction they gave
            back. Print it for the job file if a pour is ever questioned.
          </p>
        </div>
        <a href={pdfUrl()} target="_blank" rel="noreferrer" data-testid="pour-warnings-print-all">
          <Button className="rounded-none bg-primary-orange hover:bg-[rgb(var(--primary-hover))] text-black uppercase text-xs tracking-widest font-bold">
            <Printer className="w-4 h-4 mr-2" /> Print log
          </Button>
        </a>
      </div>

      <div className="card-industrial p-4 flex flex-wrap items-end gap-3">
        <div><Label className="label-tag">From</Label>
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="pw-from"
            className="rounded-none bg-transparent border-industrial mono h-9 mt-1" /></div>
        <div><Label className="label-tag">To</Label>
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="pw-to"
            className="rounded-none bg-transparent border-industrial mono h-9 mt-1" /></div>
        <Button onClick={load} disabled={loading} data-testid="pw-apply"
          className="rounded-none border border-industrial bg-transparent hover:border-primary-orange uppercase text-xs tracking-widest h-9">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Apply"}
        </Button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        {[
          ["all", "Warnings sent", sm.total ?? 0, "text-ink"],
          ["go_ahead", "Go ahead", sm.go_ahead ?? 0, "text-emerald-400"],
          ["delay", "Delay", sm.delay ?? 0, "text-red-400"],
          ["call_me", "Call me", sm.call_me ?? 0, "text-amber-400"],
          ["no_answer", "No answer", sm.no_answer ?? 0, "text-red-400"],
        ].map(([key, label, val, cls]) => (
          <button key={key} onClick={() => setFilter(key)} data-testid={`pw-tile-${key}`}
            className={`border p-3 text-left transition-colors ${filter === key ? "border-primary-orange bg-[rgb(var(--primary))]/10" : "border-industrial hover:border-primary-orange"}`}>
            <div className="mono text-[9px] uppercase tracking-widest text-muted-industrial">{label}</div>
            <div className={`mono text-2xl font-black mt-0.5 ${cls}`}>{val}</div>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 text-primary-orange animate-spin" /></div>
      ) : rows.length === 0 ? (
        <div className="card-industrial p-10 text-center" data-testid="pw-empty">
          <CloudRain className="w-8 h-8 text-muted-industrial mx-auto mb-2" />
          <div className="mono text-xs uppercase tracking-widest text-muted-industrial">No warnings in this period</div>
        </div>
      ) : (
        <div className="space-y-3" data-testid="pw-list">
          {rows.map((r, i) => {
            const o = r.reply_code ? OUTCOME[r.reply_code] : NO_ANSWER;
            return (
              <div key={`${r.job_id}-${i}`} className="card-industrial p-4" data-testid={`pw-row-${r.job_id}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="heading text-base font-black uppercase truncate">{r.customer_name || "—"}</div>
                    <div className="mono text-[11px] text-muted-industrial mt-0.5">
                      {r.quantity_m3 ?? "—"} m³ {r.mix_code || ""} · {r.date} {r.time_required || ""} · {r.site || "no site"}
                    </div>
                  </div>
                  <div className={`inline-flex items-center gap-1.5 mono text-[10px] font-black uppercase tracking-widest px-2.5 py-1 border shrink-0 ${o.cls}`}
                    data-testid={`pw-outcome-${r.job_id}`}>
                    <o.Icon className="w-3.5 h-3.5" /> {o.label}
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 mt-3 mono text-[11px]">
                  <div><span className="text-muted-industrial">Warned </span>{fmt(r.sent_at)} · to {r.sent_to || "—"} · by {r.sent_by || "—"}</div>
                  <div>
                    <span className="text-muted-industrial">Answered </span>
                    {r.reply_at ? `${fmt(r.reply_at)} · via ${r.reply_via === "web" ? "web link" : "SMS"}` : <span className="text-red-400">no reply received</span>}
                  </div>
                </div>

                {(r.reasons || []).length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2.5">
                    {r.reasons.map((x, k) => (
                      <span key={k} className="mono text-[10px] uppercase tracking-wide px-1.5 py-0.5 border border-amber-500/60 text-amber-400">{x}</span>
                    ))}
                  </div>
                )}

                <details className="mt-3 group">
                  <summary className="mono text-[10px] uppercase tracking-widest text-muted-industrial cursor-pointer hover:text-primary-orange">
                    Message sent
                  </summary>
                  <p className="text-xs text-ink mt-2 leading-relaxed border-l-2 border-industrial pl-3" data-testid={`pw-message-${r.job_id}`}>
                    {r.message || "—"}
                  </p>
                </details>

                <a href={pdfUrl(r.job_id)} target="_blank" rel="noreferrer" data-testid={`pw-print-${r.job_id}`}
                  className="inline-flex items-center gap-1.5 mono text-[10px] uppercase tracking-widest text-primary-orange hover:underline mt-3">
                  <FileText className="w-3.5 h-3.5" /> Print for job file
                </a>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
